import type { TTimer } from "@/services/timer.service"
import { env } from "@/lib/env"

export type PostMessageType =
  | "CLOCK_UPDATE"
  | "SCOREBOARD_OPENED"
  | "SCOREBOARD_READY"
  | "HEARTBEAT"

export type ClockUpdateMessage = {
  type: "CLOCK_UPDATE"
  source: "web-ovrs"
  payload: {
    unitCode: string
    time: string
    status: TTimer["status"]
    currentSplit?: number
  }
  timestamp: number
}

export type ScoreboardMessage = {
  type: "SCOREBOARD_OPENED" | "SCOREBOARD_READY"
  source: "scoreboard"
  payload: {
    scoreboardId: string
    unitCode?: string
    ready: boolean
  }
  timestamp: number
}

export type HeartbeatMessage = {
  type: "HEARTBEAT"
  source: "web-ovrs" | "scoreboard"
  payload: {
    id: string
  }
  timestamp: number
}

export type PostMessage = ClockUpdateMessage | ScoreboardMessage | HeartbeatMessage

type PostMessageData = {
  type: "clockUpdate" | "reconnect" | "reconnectAck" | "parentReloading"
  payload?: ClockUpdateMessage["payload"]
  unitCode?: string
  channelCode?: string
  timestamp?: number
}

// TODO: Metodo para limpiar los listeners y separar los callbacks de los eventos
class PostMessageService {
  private static instance: PostMessageService
  private listeners: Map<PostMessageType, Set<(message: PostMessage) => void>> = new Map()
  private isListening = false
  private connectedScoreboards = new Set<string>()
  private scoreboardWindows = new Map<string, Window>()
  private reconnectionTimeouts = new Map<string, NodeJS.Timeout>()

  private constructor() {
    this.setupMessageListener()
    this.setupBeforeUnloadHandler()
  }

  static getInstance(): PostMessageService {
    if (!PostMessageService.instance) {
      PostMessageService.instance = new PostMessageService()
    }
    return PostMessageService.instance
  }

  /**
   * Registrar una ventana de scoreboard abierta
   */
  registerScoreboardWindow(channelCode: string, windowRef: Window) {
    this.scoreboardWindows.set(channelCode, windowRef)
    console.log(`📱 Registered scoreboard window for channel: ${channelCode}`)
    console.log(`📊 Total registered windows: ${this.scoreboardWindows.size}`)

    // Limpiar la referencia cuando la ventana se cierre
    const checkClosed = () => {
      if (windowRef.closed) {
        this.scoreboardWindows.delete(channelCode)
        console.log(`🗑️ Removed closed scoreboard window: ${channelCode}`)
        console.log(`📊 Remaining windows: ${this.scoreboardWindows.size}`)
      }
      else {
        setTimeout(checkClosed, 1000) // Verificar cada segundo
      }
    }
    setTimeout(checkClosed, 1000)
  }

  /**
   * Enviar mensaje de actualización de reloj
   */
  sendClockUpdate(clockData: ClockUpdateMessage["payload"]) {
    const message: ClockUpdateMessage = {
      type: "CLOCK_UPDATE",
      source: "web-ovrs",
      payload: clockData,
      timestamp: Date.now(),
    }

    try {
      // 1. Enviar a ventanas específicas de scoreboards (prioridad alta)
      this.scoreboardWindows.forEach((windowRef, channelCode) => {
        try {
          const scoreboardUrl: string = env.NEXT_PUBLIC_SCOREBOARD_URL || "https://devscb.cis2025.win2tec.es"
          if (!windowRef.closed) {
            windowRef.postMessage(message, `${scoreboardUrl}/scoreboards/${channelCode}`) // Usar * para ventanas cross-origin
            // console.log(`🎯 Clock update sent to specific window: ${channelCode}`)
          }
          else {
            // Limpiar ventana cerrada
            this.scoreboardWindows.delete(channelCode)
          }
        }
        catch (error) {
          console.warn(`Error sending to window ${channelCode}:`, error)
        }
      })

      // 2. Fallback: PostMessage local para misma ventana/iframe
      window.postMessage(message, window.origin)
    }
    catch (error) {
      console.error("Error sending clock update:", error)
      throw error
    }
  }

  /**
   * Obtener ventanas registradas
   */
  getRegisteredWindows(): string[] {
    return Array.from(this.scoreboardWindows.keys())
  }

  private setupBeforeUnloadHandler() {
    if (typeof window === "undefined") {
      console.warn("⚠️ Window is undefined, skipping beforeunload setup")
      return
    }

    console.log("🔧 Setting up beforeunload and unload handlers...")

    // Manejar el evento beforeunload (se dispara antes de recargar/cerrar)
    window.addEventListener("beforeunload", (_event) => {
      console.log("🔄 BEFOREUNLOAD: Web-OVRS is about to reload/close, notifying scoreboards...")
      console.log(`📊 Currently registered windows: ${this.scoreboardWindows.size}`, Array.from(this.scoreboardWindows.keys()))

      this.notifyScoreboardsReloading()

      // No prevenir el unload, solo notificar
    })

    // También manejar unload como fallback
    window.addEventListener("unload", () => {
      console.log("🔄 UNLOAD: Web-OVRS is unloading, final notification to scoreboards...")
      this.notifyScoreboardsReloading()
    })

    // Manejar visibilitychange como adicional para cambios de pestaña
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") {
        console.log("👁️ Page hidden, might be closing...")
      }
    })

    console.log("✅ Beforeunload handlers setup complete")
  }

  private notifyScoreboardsReloading() {
    // Notificar a todos los scoreboards que nos vamos a recargar
    const reloadMessage: PostMessageData = {
      type: "parentReloading",
      timestamp: Date.now(),
    }

    console.log(`📤 Sending reload notifications to ${this.scoreboardWindows.size} scoreboards...`)

    // Enviar via postMessage a ventanas específicas
    this.scoreboardWindows.forEach((window, channelCode) => {
      try {
        if (!window.closed) {
          window.postMessage(reloadMessage, "*")
          console.log(`📤 Sent reload notification to ${channelCode}`)
        }
        else {
          console.log(`⚠️ Window ${channelCode} is already closed`)
        }
      }
      catch (error) {
        console.warn(`Failed to notify ${channelCode}:`, error)
      }
    })
  }

  private setupMessageListener() {
    if (typeof window === "undefined")
      return

    window.addEventListener("message", (event) => {
      try {
        const data = event.data as PostMessageData

        if (data.type === "reconnect" && data.channelCode) {
          console.log(`🔄 Received reconnection request from ${data.channelCode}`)
          this.handleReconnection(event, data.channelCode)
        }
      }
      catch (error) {
        console.warn("Error processing postMessage:", error)
      }
    })
  }

  private handleReconnection(event: MessageEvent, channelCode: string) {
    if (!event.source || typeof event.source.postMessage !== "function") {
      console.warn("⚠️ Reconnection event source is not a valid window")
      return
    }

    console.log(`✅ Restoring window reference for ${channelCode} using event.source`)

    // Restaurar la referencia usando event.source
    this.scoreboardWindows.set(channelCode, event.source as Window)

    // Enviar confirmación de reconexión
    const message: PostMessageData = {
      type: "reconnectAck",
      channelCode,
      timestamp: Date.now(),
    }

    try {
      event.source.postMessage(message, {
        targetOrigin: "*",
      })
      console.log(`📤 Sent reconnection acknowledgment to ${channelCode}`)
    }
    catch (error) {
      console.error("Failed to send reconnection ack:", error)
    }
  }
}

export const postMessageService = PostMessageService.getInstance()
